import { FC } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import { EditorialFacebookPostNetwork } from '@/utils/enums';

import { ActionType } from './MoreActions';

const StyledSidePanelContent = styled(SidePanelContent)`
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const CloseButton = styled(Button)`
  grid-column: span 2;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ActionButton = styled(Text)`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 46px;
  padding: 0 16px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};

  &:hover {
    color: ${({ theme }) => theme.colors.black};
    border-color: ${({ theme }) => theme.colors.gray};
  }
`;

interface Props {
  onClose: VoidFunction;
  onActionClick: (actionType: ActionType) => void;
}

const MoreActionsDrawer: FC<Props> = ({ onClose, onActionClick }) => {
  const [network] = useNetwork();

  return (
    <Modal>
      <SidePanel heading="More actions" onClose={onClose}>
        <SidePanelBody as="div">
          <StyledSidePanelContent>
            <Group>
              <Text $type="smallBold">Uploads & Exports</Text>
              <ActionButton
                as="button"
                type="button"
                $type="captionSemibold"
                $color="gray"
                onClick={() => onActionClick('upload-csv')}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.0625 5.89063L10 2.14062L13.9375 5.89063"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 12.1445V2.14453"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17.748 10.7148V17.1434C17.748 17.3329 17.68 17.5145 17.5589 17.6485C17.4378 17.7824 17.2735 17.8577 17.1022 17.8577H2.89388C2.72259 17.8577 2.55832 17.7824 2.43721 17.6485C2.31609 17.5145 2.24805 17.3329 2.24805 17.1434V10.7148"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Upload CSV
              </ActionButton>
              <ActionButton
                as="button"
                type="button"
                $type="captionSemibold"
                $color="gray"
                onClick={() => onActionClick('download-csv')}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.7857 2.5H3.21429C2.8198 2.5 2.5 2.8198 2.5 3.21429V16.7857C2.5 17.1802 2.8198 17.5 3.21429 17.5H16.7857C17.1802 17.5 17.5 17.1802 17.5 16.7857V3.21429C17.5 2.8198 17.1802 2.5 16.7857 2.5Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 2.5V17.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M11.0713 2.5L17.5004 8.92909"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 6.42969L17.5 13.9297"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 11.4297L16.0709 17.5006"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Download CSV Template
              </ActionButton>
              <ActionButton
                as="button"
                type="button"
                $type="captionSemibold"
                $color="gray"
                onClick={() => onActionClick('export-csv')}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.40151 1.75C5.9873 1.75 5.65151 2.08579 5.65151 2.5C5.65151 2.91421 5.9873 3.25 6.40151 3.25H13.0396L4.4651 11.9743C4.17475 12.2697 4.17886 12.7446 4.47428 13.0349C4.7697 13.3252 5.24455 13.3211 5.5349 13.0257L14.2513 4.15707V11.25C14.2513 11.6642 14.5871 12 15.0013 12C15.4155 12 15.7513 11.6642 15.7513 11.25V2.5C15.7513 2.08579 15.4155 1.75 15.0013 1.75H14.8284H6.40151Z"
                    fill="currentColor"
                  />
                  <path
                    d="M3.75 17.5H16.25"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Export Posts CSV
              </ActionButton>
              <ActionButton
                as="button"
                type="button"
                $type="captionSemibold"
                $color="gray"
                onClick={() => onActionClick('gsheet-import')}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.125 2.5H16.875C17.2202 2.5 17.5 2.7798 17.5 3.125V16.875C17.5 17.2202 17.2202 17.5 16.875 17.5H3.125C2.7798 17.5 2.5 17.2202 2.5 16.875V3.125C2.5 2.7798 2.7798 2.5 3.125 2.5Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2.5 7.5H17.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2.5 12.5H17.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7.5 7.5V17.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Run GSheet Video Import
              </ActionButton>
            </Group>
            <Group>
              <Text $type="smallBold">Performance & Other:</Text>
              <ActionButton
                as="button"
                type="button"
                $type="captionSemibold"
                $color="gray"
                onClick={() => onActionClick('performance')}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.125 4.375L10.625 11.875L7.5 8.75L1.875 14.375"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M18.125 9.375V4.375H13.125"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Performance
              </ActionButton>
              <ActionButton
                as="button"
                type="button"
                $type="captionSemibold"
                $color="gray"
                onClick={() => onActionClick('overview')}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.07129 8.92969H11.7856"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8.92871 6.07227V11.7866"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8.92773 16.4297C13.0699 16.4297 16.4277 13.0718 16.4277 8.92969C16.4277 4.78755 13.0699 1.42969 8.92773 1.42969C4.7856 1.42969 1.42773 4.78755 1.42773 8.92969C1.42773 13.0718 4.7856 16.4297 8.92773 16.4297Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14.2305 14.2305L18.5698 18.5698"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Pages Overview
              </ActionButton>
              <ActionButton
                as="button"
                type="button"
                $type="captionSemibold"
                $color="gray"
                onClick={() => onActionClick('pages-modal')}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.5 3.75H17.5V15.5556C17.5 15.7397 17.4342 15.9164 17.3169 16.0466C17.1997 16.1768 17.0408 16.25 16.875 16.25H3.125C2.95924 16.25 2.80027 16.1768 2.68306 16.0466C2.56585 15.9164 2.5 15.7397 2.5 15.5556V3.75Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2.5 8.125H17.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2.5 11.875H17.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6.875 8.125V15.625"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Pages, Groups & Boards IDs
              </ActionButton>
              {[
                EditorialFacebookPostNetwork.FACEBOOK,
                EditorialFacebookPostNetwork.INSTAGRAM,
              ].includes(network) && (
                <ActionButton
                  as="button"
                  type="button"
                  $type="captionSemibold"
                  $color="gray"
                  onClick={() => onActionClick('resorter-settings')}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    width={20}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 7.5 7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5"
                    />
                  </svg>
                  Resorter Settings
                </ActionButton>
              )}
              <ActionButton
                as="button"
                type="button"
                $type="captionSemibold"
                $color="gray"
                onClick={() => onActionClick('blacklist')}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  width={20}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"
                  />
                </svg>
                Blacklisted Keywords
              </ActionButton>
              <ActionButton
                as="button"
                type="button"
                $type="captionSemibold"
                $color="gray"
                onClick={() => onActionClick('notifications-settings')}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  width={20}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0M3.124 7.5A8.969 8.969 0 0 1 5.292 3m13.416 0a8.969 8.969 0 0 1 2.168 4.5"
                  />
                </svg>
                Notifications
              </ActionButton>
              {network === EditorialFacebookPostNetwork.FACEBOOK && (
                <ActionButton
                  as="button"
                  type="button"
                  $type="captionSemibold"
                  $color="gray"
                  onClick={() => onActionClick('repost-automation')}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    width={20}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                    />
                  </svg>
                  Repost Automation
                </ActionButton>
              )}
            </Group>
          </StyledSidePanelContent>
          <SidePanelFooter $actions>
            <CloseButton color="secondary" onClick={onClose} type="button">
              Close
            </CloseButton>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default MoreActionsDrawer;
